import { Button, DialogContent, DialogTitle, Stack, TextField } from "@mui/material";
import { useMemo, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import {
  combineValidators,
  regexValidator,
  requiredValidator,
  uniqueValidator,
} from "@entrilia/shared/utilities/validators";
import adminApi from "../../../../api/adminApi";
import UserConsentCategorySelect from "./UserConsentCategorySelect";

interface Props {
  onClose: () => void;
  onCreated: (newConsentName: string) => void;
  existingConsentNames: string[];
}

const createUserConsent = withErrorHandling(adminApi.createUserConsent);

const CreateUserConsentDialog = ({ onClose, onCreated, existingConsentNames }: Props) => {
  const { sendNotificationError } = useNotificationContext();

  const validateConsentName = useMemo(
    () =>
      combineValidators(
        requiredValidator,
        regexValidator(/^[a-zA-Z0-9\s]+$/, "Name can only contain letters, numbers and spaces"),
        uniqueValidator(
          "User consent with such name already exists",
          existingConsentNames.map((n) => n.toLowerCase())
        )
      ),
    [existingConsentNames]
  );

  const [name, setName] = useState("");
  const [isDirty, setIsDirty] = useState(false);
  const [categoryId, setCategoryId] = useState<string>();
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    setSaving(true);
    const [newConsentName, error] = await createUserConsent({ name, categoryId });
    setSaving(false);

    if (error) {
      logError(error, "[CreateUserConsentDialog] deleteUserConsent");
      sendNotificationError(error.message);
      return;
    }

    onCreated(newConsentName);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsDirty(true);
    setName(e.target.value);
  };

  const nameValidationResult = validateConsentName(name.toLowerCase());

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>New User Consent</DialogTitle>
      <DialogCloseButton onClick={onClose} />

      <DialogContent>
        <Stack spacing={2}>
          <TextField
            fullWidth
            label="Name"
            value={name}
            onChange={handleNameChange}
            error={isDirty && !nameValidationResult.isValid}
            helperText={isDirty ? nameValidationResult.error : undefined}
          />

          <UserConsentCategorySelect
            categoryId={categoryId}
            onChange={(newCategoryId) => setCategoryId(newCategoryId)}
          />
        </Stack>
      </DialogContent>

      <DialogButtons onCancel={onClose}>
        <Button variant="contained" loading={saving} onClick={handleSubmit} disabled={!nameValidationResult.isValid}>
          Add
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default CreateUserConsentDialog;
